import React, { useMemo } from 'react';
import { Calendar, Heart, Star, Baby, Camera, Globe } from 'lucide-react';
import { FamilyMember, Post } from '../types';

interface TimelineProps {
  members: Record<string, FamilyMember>;
  posts: Post[];
}

// --- WORLD EVENTS CONTEXT (The "Wow" Factor) ---
const WORLD_EVENTS = [
  { year: 1945, title: "End of WWII", type: 'world' },
  { year: 1969, title: "Moon Landing", type: 'world' },
  { year: 1989, title: "Fall of Berlin Wall", type: 'world' },
  { year: 2000, title: "The New Millennium", type: 'world' },
  { year: 2007, title: "First iPhone Released", type: 'world' },
  { year: 2020, title: "Global Pandemic", type: 'world' },
];

const FamilyTimeline: React.FC<TimelineProps> = ({ members, posts }) => {
  // Safety check - if members are missing, show loading state
  // --- ENGINE: Merge & Sort History ---
  const timelineEvents = useMemo(() => {
    if (!members || Object.keys(members).length === 0) return [];
    let events: any[] = [];

    // 1. Add Birthdays
    Object.values(members).forEach(m => {
      if (m.dob) {
        events.push({
          id: `birthday-${m.id}`,
          year: new Date(m.dob).getFullYear(),
          date: m.dob,
          title: `Birthday: ${m.name.split(' ')[0]}`,
          type: 'birthday',
          image: m.avatarUrl,
          personId: m.id
        });
      }
      // 2. Add Weddings (only if not deceased)
      if (m.weddingDate && m.spouseIds && m.spouseIds.length > 0 && !m.isDeceased) {
        const spouseName = members[m.spouseIds[0]]?.name || "Spouse";
        if (m.id < m.spouseIds[0]) {
           events.push({
             id: `wedding-${m.id}-${m.spouseIds[0]}`,
             year: new Date(m.weddingDate).getFullYear(),
             date: m.weddingDate,
             title: `Wedding: ${m.name.split(' ')[0]} & ${spouseName.split(' ')[0]}`,
             type: 'wedding',
             image: null
           });
        }
      }
      // 2b. Add Divorces (new marital status)
      if ((m.maritalStatus === 'Divorced' || m.divorceDate) && m.divorceDate && !m.isDeceased) {
        events.push({
          id: `divorce-${m.id}`,
          year: new Date(m.divorceDate).getFullYear(),
          date: m.divorceDate,
          title: `Divorce: ${m.name.split(' ')[0]}`,
          type: 'divorce',
          image: m.avatarUrl,
          personId: m.id
        });
      }
      // 3. Add Demise
      if (m.isDeceased && m.demiseDate) {
        events.push({
          id: `demise-${m.id}`,
          year: new Date(m.demiseDate).getFullYear(),
          date: m.demiseDate,
          title: `Demise: ${m.name.split(' ')[0]}`,
          type: 'demise',
          image: m.avatarUrl,
          personId: m.id
        });
      }
    });

    // 4. Add World Events (Context)
    WORLD_EVENTS.forEach(we => {
       events.push({ ...we, date: `${we.year}-12-31`, id: `world-${we.year}` });
    });

    // SORT: Newest First
    return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [members, posts]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'birth': return <Baby className="w-5 h-5 text-indigo-600" />;
      case 'birthday': return <Baby className="w-5 h-5 text-indigo-600" />;
      case 'wedding': return <Heart className="w-5 h-5 text-pink-600" />;
      case 'divorce': return <Heart className="w-5 h-5 text-red-500 opacity-50" style={{transform: 'scaleX(1.2)'}} />;
      case 'post': return <Camera className="w-5 h-5 text-emerald-600" />;
      case 'demise': return <Star className="w-5 h-5 text-gray-400" />;
      case 'world': return <Globe className="w-5 h-5 text-gray-400" />;
      default: return <Star className="w-5 h-5 text-amber-500" />;
    }
  };

  return (
    <div className="max-w-3xl mx-auto pb-20 bg-gray-50 min-h-screen w-full overflow-x-hidden">
      
      {/* Header */}
      <div className="bg-white p-8 rounded-b-3xl shadow-sm border-b border-gray-200 mb-10 text-center">
         <h1 className="text-3xl font-black text-gray-900 mb-2">Family Chronicles</h1>
         <p className="text-gray-500">A journey through time across {timelineEvents.length} milestones.</p>
      </div>

      {/* Timeline Stream */}
      <div className="relative px-4">
        {/* The Vertical Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {timelineEvents.map((event, index) => (
          <div key={event.id} className="relative pl-16 mb-12 group animate-slide-up" style={{ animationDelay: `${index * 0.05}s` }}>
            
            {/* The Dot */}
            <div className={`absolute left-4 -translate-x-1/2 w-8 h-8 rounded-full border-4 border-gray-50 flex items-center justify-center shadow-md z-10 ${
               event.type === 'world' ? 'bg-gray-200' : 'bg-white'
            }`}>
               {getIcon(event.type)}
            </div>

            {/* The Card */}
            {event.type === 'world' ? (
               // World Event Styling (Subtle)
               <div className="flex items-center gap-4 opacity-70 hover:opacity-100 transition-opacity">
                  <span className="text-2xl font-black text-gray-300">{event.year}</span>
                  <div className="bg-gray-200 px-3 py-1 rounded-full text-xs font-bold text-gray-600 uppercase tracking-wider">
                     Global Context: {event.title}
                  </div>
               </div>
            ) : (
               // Family Event Styling (Rich)
               <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative">
                  {/* Year Tag */}
                  <div className="absolute -top-3 right-4 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                     {event.year}
                  </div>

                  <h3 className="font-bold text-lg text-gray-900">{event.title}</h3>
                  {event.content && <p className="text-gray-600 text-sm mt-2 italic">"{event.content}"</p>}
                  
                  {/* Photo attachment */}
                  {event.image && (
                     <div className="mt-4 rounded-xl overflow-hidden h-48 w-full relative">
                        <img src={event.image} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                     </div>
                  )}

                  {/* Footer Stats (Mocked for visuals) */}
                  <div className="flex gap-4 mt-4 pt-4 border-t border-gray-50 text-xs font-bold text-gray-400">
                     <span className="flex items-center gap-1 hover:text-pink-500 cursor-pointer"><Heart className="w-3 h-3" /> Like</span>
                     <span className="flex items-center gap-1 hover:text-indigo-500 cursor-pointer"><Calendar className="w-3 h-3" /> {new Date(event.date).toLocaleDateString()}</span>
                  </div>
               </div>
            )}
          </div>
        ))}

        {timelineEvents.length === 0 && (
           <div className="text-center py-20 opacity-50">
              <p>Add members and birth years to see your history unfold.</p>
           </div>
        )}
      </div>
    </div>
  );
};

export default FamilyTimeline;